import { useEffect, useMemo, useRef, useState } from "react";
import { CATEGORY_ORDER, filterItems, searchItems } from "../../data/catalog";
import { plural } from "../../utils/format";
import ItemCard from "../catalog/ItemCard";

// Full-screen item search across the whole inventory.
export default function InventorySearch({ query, onQueryChange, onClose }) {
  const inputRef = useRef(null);
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef(null);
  const hoverTimer = useRef(null);
  const dismiss = () => {
    if (closeTimer.current) return;
    setClosing(true);
    closeTimer.current = setTimeout(onClose, 160);
  };
  useEffect(() => () => {
    clearTimeout(closeTimer.current);
    clearTimeout(hoverTimer.current);
  }, []);
  const term = query.trim();
  const results = useMemo(() => searchItems(term), [term]);

  useEffect(() => {
    const input = inputRef.current;
    input?.focus();
    input?.setSelectionRange(input.value.length, input.value.length);
  }, []);

  let meta = "New arrivals";
  let body;
  if (!term) {
    body = CATEGORY_ORDER.filter(department => department !== "All").map(department => {
      const item = filterItems({ department, sort: "new" })[0];
      return item ? <div className="searchDepartmentPreview" key={department}>
        <div className="searchDepartmentLabel">{department}</div>
        <ItemCard item={item} showStock={false} />
      </div> : null;
    });
  } else {
    meta = `${plural(results.length, "item")} found for “${term}”`;
    body = results.length ? (
      results.map((item) => <ItemCard key={item.id} item={item} showStock={false} />)
    ) : (
      <div className="v134SearchEmpty">No items found for “{term}”.</div>
    );
  }

  return (
    <>
    <div className={`searchBackdrop${closing ? " closing" : ""}`} onClick={dismiss}
      onMouseEnter={() => { hoverTimer.current = setTimeout(dismiss, 60); }}
      onMouseLeave={() => clearTimeout(hoverTimer.current)} aria-hidden="true" />
    <div className={`v134SearchPanel on${closing ? " closing" : ""}`} id="v134SearchPanel" role="dialog" aria-modal="true" aria-label="Search products">
      <div className="v134SearchInner">
        <div className="v134SearchHead">
          <button type="button" className="mobileSearchClose" aria-label="Close search" onClick={dismiss}>×</button>
          <div className="v134SearchLine"
            onMouseEnter={() => inputRef.current?.focus()}
            onClick={(e) => {
              if (!e.target.closest("button")) inputRef.current?.focus();
            }}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M15.5 15.5L21 21" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              ref={inputRef}
              id="v134SearchInput"
              className="v134SearchInput"
              type="search"
              inputMode="search"
              enterKeyHint="search"
              placeholder="Search"
              autoComplete="off"
              aria-label="Search products"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.target.blur();
                }
              }}
            />
          </div>
        </div>
        <div id="v134SearchMeta" className="v134SearchMeta">
          {meta}
        </div>
        <div id="v134SearchGrid" className="v134SearchGrid">
          {body}
        </div>
      </div>
    </div>
    </>
  );
}
